import Head from 'next/head';
import Link from 'next/link';
import { useState, useEffect, type ReactElement } from 'react';
import { useRouter } from 'next/router';
import { User } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

export default function IndividualSignup(): ReactElement {
  const router = useRouter();
  useEffect((): (() => void) => {
    const run = async (): Promise<void> => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) router.replace('/dashboard');
    };
    void run();
    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session) router.replace('/dashboard');
    });
    return () => listener.subscription.unsubscribe();
  }, [router]);
  
  useEffect((): void => {
    if (router.query.email) setEmail(String(router.query.email));
  }, [router.query.email]);
  
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreateAccount(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) throw signUpError;
      alert('Account created. Check your email to confirm, then log in.');
      router.push('/individual/login');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Head>
        <title>Individual Signup – KEM Discovery</title>
      </Head>
      <section className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-purple-50/30 dark:from-gray-900 dark:to-purple-950/30">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto size-16 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white grid place-items-center shadow-lg mb-4">
                <User className="size-8" />
              </div>
              <CardTitle className="text-2xl font-bold leading-tight">
                <span className="block">Personal</span>
                <span className="block">Discovery</span>
                <span className="block">Sign Up</span>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">Create your account to begin your personalized journey</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && <p className="text-sm text-destructive mt-2">{error}</p>}
              <form onSubmit={handleCreateAccount} className="mt-2 space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input 
                    id="signup-email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    required 
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirm">Confirm Password</Label>
                  <Input 
                    id="confirm" 
                    type="password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    required 
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Creating Account…' : 'Create Account'}
                </Button>
              </form>
              <p className="mt-2 text-sm text-center">
                Already have an account?{' '}
                <Link href="/individual/login" className="text-purple-600 hover:text-purple-500 font-medium underline underline-offset-4">Log in</Link>
              </p>
            </CardContent>
          </Card>
          <p className="mt-3 text-center">
            <AuthAwareBackLink />
          </p>
        </div>
      </section>
    </>
  );
}

function AuthAwareBackLink(): ReactElement {
  const [user, setUser] = useState<unknown | null>(null);
  useEffect((): (() => void) => {
    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => setUser(session?.user ?? null));
    return (): void => listener.subscription.unsubscribe();
  }, []);
  return user ? (
    <Button asChild variant="ghost" size="sm">
      <Link href="/dashboard">Go to dashboard</Link>
    </Button>
  ) : (
    <Button asChild variant="ghost" size="sm">
      <Link href="/">&larr; Back to home</Link>
    </Button>
  );
}

